import * as React from "react";
import {Fragment, Suspense} from "react";
import {Accordion, Button, Collapse} from "react-bootstrap";
import {FaComment, FaPlusCircle} from "react-icons/fa";
import {useQuestionsQuery} from "../../../hooks/useQuestionsQuery";
import {SkeletonAccordionBody} from "../../../components/skeletonAccordionBody";
import Skeleton from "react-loading-skeleton";
import {useShowMoreQuestions} from "../../../hooks/useShowMoreQuestions";
import {ZeroQuestions} from "./zeroQuestions";
import {CustomerServiceNotice} from "./customerServiceNotice";
import {AskQuestionForm} from "./askQuestionForm";

export const Questions = () => {
    const {
        open,
        setOpen,
        questionEdges,
        breakOutEarly,
        fetchNextPage,
        fetchNextPageLoading,
        loading
    } = useQuestionsQuery();

    if (loading) return <SkeletonAccordionBody/>;
    if (breakOutEarly) return <ZeroQuestions/>;

    return (
        <>
            <Accordion id="qa-accordion" flush alwaysOpen>
                {questionEdges.map(({
                                        node: {
                                            text,
                                            byline,
                                            answers,
                                            hasAnswers,
                                            formattedAnsweredCount
                                        }
                                    }, index) => (
                    <Accordion.Item key={String(index)} eventKey={String(index)}>
                        <Accordion.Button className="py-0 question d-flex justify-content-between">
                            <div className="p-3 d-flex">
                                <div className="me-2 fw-bold">Q:</div>
                                <div>
                                    {text}
                                    <small className="text-muted d-block mt-1">
                                        {byline}
                                    </small>
                                </div>
                            </div>
                            {hasAnswers && (
                                <div className="mx-2 answer-count" style={{whiteSpace: 'nowrap'}}>
                                    <strong className="text-info has-answers">
                                        {formattedAnsweredCount}
                                    </strong>
                                </div>
                            )}
                        </Accordion.Button>
                        <Accordion.Body className="pt-0">
                            {answers.map(({id, text, byline, commentButtonText, hasComments, comments}) => (
                                <Fragment key={id}>
                                    <div className="bg-light border-bottom rounded p-3 mx-4">
                                        <div className="d-flex answer">
                                            <div className="me-2 fw-bold">A:</div>
                                            <div>
                                                {text}
                                                <small className="text-muted d-block mt-1">
                                                    {byline}
                                                </small>
                                            </div>
                                        </div>
                                        {hasComments && (
                                            <div className="text-end">
                                                <Button
                                                    variant="link"
                                                    onClick={() => setOpen(!open)}
                                                    aria-controls={`answer-comments-${id}`}
                                                    aria-expanded={open}
                                                >
                                                    <FaComment className="me-1"/>
                                                    {commentButtonText}
                                                </Button>
                                                <Collapse in={open} className="bg-light text-start">
                                                    <div id={`answer-comments-${id}`}>
                                                        {comments.map(({id, text, byline}) =>
                                                            <div key={String(id)}
                                                                 className="d-flex comment p-3 border-bottom ms-3">
                                                                <div className="me-2 fw-bold">
                                                                    Comment:{' '}
                                                                </div>
                                                                <div>
                                                                    {text}
                                                                    <small
                                                                        className="text-muted d-block mt-1">
                                                                        {byline}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Collapse>
                                            </div>
                                        )}
                                    </div>
                                </Fragment>
                            ))}
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
            <hr/>
            <ShowMore disabled={fetchNextPageLoading} onClick={fetchNextPage}/>
            <div className="border-top p-3 text-center">
                <strong className="fs-4">Don't see the answer you're looking for? Ask a Question!</strong>
                <CustomerServiceNotice/>
                <Suspense>
                    <AskQuestionForm/>
                </Suspense>
            </div>
        </>
    )
}

const ShowMore = ({disabled, onClick}: { disabled: boolean, onClick: () => Promise<void> }) => {
    const {loading, hasNextPage, headingTitle, buttonTitle} = useShowMoreQuestions();

    if (loading) {
        return (
            <div className="text-center mb-3">
                <Skeleton height={30}/>
            </div>
        )
    }

    return (
        <div className="d-flex flex-column align-items-center">
            <div className="mb-3">  {headingTitle}
                <span className="d-none d-sm-inline">questions</span></div>
            {hasNextPage && (
                <>
                    <Button
                        variant="primary"
                        className="mb-3 d-flex align-items-center"
                        disabled={disabled}
                        onClick={onClick}>
                        <FaPlusCircle size={20} className="me-1" aria-hidden/>
                        {buttonTitle}
                    </Button>
                </>
            )}
        </div>
    );
};